import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { TrendingUp, FileText } from 'lucide-react';
import { useSite } from '../../contexts/SiteContext';

export const SCJointInjury = () => {
  const { theme } = useSite();
  
  const faqs = [
    { question: 'What is the SC joint?', answer: 'The sternoclavicular (SC) joint is where the clavicle meets the sternum (breastbone). It is the only bony connection between the arm and the axial skeleton. Despite high mobility demands, SC joint injuries are uncommon (3% of shoulder girdle injuries).' },
    { question: 'What are the types of SC joint injuries?', answer: 'Anterior dislocation (more common, less serious): Clavicle displaces forward. Posterior dislocation (rare but serious): Clavicle displaces backward toward vital structures (trachea, great vessels). Requires urgent evaluation and often emergency reduction.' },
    { question: 'Is SC joint dislocation dangerous?', answer: 'Anterior dislocations are usually not dangerous and often treated non-operatively. Posterior dislocations can be life-threatening as the clavicle can compress the trachea, esophagus, or major blood vessels. Requires immediate medical attention.' },
    { question: 'How are SC joint injuries treated?', answer: 'Anterior: Usually conservative (sling, pain management). Posterior: May require urgent closed reduction in OR or open reduction. Chronic instability: Rarely requires surgical reconstruction.' }
  ];

  return (
    <div className="min-h-screen" data-testid="sc-joint-page">
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${theme?.primaryHex}20`, color: theme?.primaryHex }}>
              Trauma & Fractures
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">SC Joint Injury Treatment in Hyderabad</h1>
            <p className="text-xl text-gray-600">Sternoclavicular dislocation management by Dr. Deepthi Nandan Reddy</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding SC Joint Injuries</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>The SC joint is stabilized by strong ligaments making dislocation uncommon. High-energy mechanisms are usually required: motor vehicle accidents, sports collisions (lateral compression to shoulder), or direct blows.</p>
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r">
                <p className="font-semibold text-red-800">⚠️ Emergency Warning</p>
                <p className="text-red-700">Posterior SC dislocation is a surgical emergency. Symptoms of airway compromise (difficulty breathing, stridor) or vascular injury (pulse changes, swelling) require immediate evaluation.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Treatment</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-l-4 border-l-green-500">
                <CardHeader><CardTitle>Anterior Dislocation</CardTitle></CardHeader>
                <CardContent className="text-gray-700">
                  <p>• Usually non-operative</p>
                  <p>• Sling for comfort (2-4 weeks)</p>
                  <p>• Ice and NSAIDs</p>
                  <p>• Closed reduction rarely needed</p>
                  <p>• May have persistent cosmetic bump</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-red-500">
                <CardHeader><CardTitle>Posterior Dislocation</CardTitle></CardHeader>
                <CardContent className="text-gray-700">
                  <p>• Urgent evaluation required</p>
                  <p>• CT angiogram to assess vessels</p>
                  <p>• Closed reduction in OR (with thoracic surgery standby)</p>
                  <p>• Open reduction if closed fails</p>
                  <p>• May need ligament reconstruction</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <CTASection />
    </div>
  );
};

export default SCJointInjury;
